import { format } from 'date-fns';
import { openPrintWindow } from './printWindow';
import {
  DATE_FIELDS, INCLUDES, STATUS_LABELS, dayLong, dayShort, num, optionLabel, weekRange, weekTitle,
} from '../components/weekly-plan/weeklyPlanUtils';

const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const STYLE = `
  @page { size: A4 landscape; margin: 10mm; }
  body { font-family: Arial, sans-serif; color: #111; margin: 0; padding: 16px; font-size: 12px; }
  .week { page-break-after: always; }
  .week:last-child { page-break-after: auto; }
  h1 { font-size: 20px; margin: 0; }
  .sub { color: #555; margin: 2px 0 12px; }
  .stats { display: flex; gap: 28px; margin-bottom: 14px; }
  .stat b { display: block; font-size: 20px; }
  .stat span { font-size: 11px; color: #666; }
  h2 { font-size: 14px; margin: 16px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #bbb; padding: 4px 6px; vertical-align: top; }
  th { background: #eee; font-size: 11px; text-transform: uppercase; text-align: left; }
  td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { font-weight: bold; background: #f4f4f4; }
  .day { page-break-inside: avoid; }
  .day h3 { font-size: 13px; margin: 12px 0 4px; padding: 4px 6px; background: #e8f1f3; border-left: 4px solid #005F73; }
  .muted { color: #888; }
  .foot { margin-top: 16px; font-size: 10px; color: #999; }
`;

const gridSection = (w) => {
  const head = w.days.map((d) => `<th class="n">${esc(dayShort(d.date))}</th>`).join('');
  const rows = w.products.map((p) => `<tr><td>${esc(p.product_name)}</td>${w.days.map((d) => {
    const v = p.by_day[d.date];
    return `<td class="n">${v ? num(v, 1) : ''}</td>`;
  }).join('')}<td class="n"><b>${num(p.units, 1)}</b></td></tr>`).join('');
  const totals = w.days.map((d) => `<td class="n">${d.units ? num(d.units, 1) : ''}</td>`).join('');
  return `<h2>Units by product and day</h2><table><thead><tr><th>Product</th>${head}<th class="n">Week</th></tr></thead><tbody>
    ${rows || `<tr><td colspan="9" class="muted">No orders this week.</td></tr>`}
    <tr class="total"><td>Day total</td>${totals}<td class="n">${num(w.units, 1)}</td></tr></tbody></table>`;
};

const orderRows = (o) => o.lines.map((ln, i) => `<tr>
  ${i === 0 ? `<td rowspan="${o.lines.length}"><b>${esc(o.customer_name)}</b><br><span class="muted">#${esc(o.dispatch_number)}</span></td>
  <td rowspan="${o.lines.length}">${esc(o.po_number || '-')}</td>` : ''}
  <td>${esc(ln.product_name)}</td><td class="n"><b>${num(ln.units, 1)}</b></td><td class="n">${num(ln.cases, 1)}</td><td class="n">${num(ln.weight, 1)}</td>
  ${i === 0 ? `<td rowspan="${o.lines.length}">${esc(o.vehicle_info || '-')}</td><td rowspan="${o.lines.length}">${esc(STATUS_LABELS[o.status] || o.status)}</td>` : ''}
</tr>`).join('');

const ordersSection = (w) => {
  const days = w.days.filter((d) => d.orders.length);
  if (!days.length) return '<h2>Orders by day</h2><p class="muted">No orders this week.</p>';
  return `<h2>Orders by day</h2>${days.map((d) => `<div class="day">
    <h3>${esc(dayLong(d.date))} — ${d.order_count} order${d.order_count === 1 ? '' : 's'} · ${num(d.units, 1)} units · ${num(d.cases, 1)} cases · ${num(d.weight, 1)} kg</h3>
    <table><thead><tr><th>Customer</th><th>PO #</th><th>Product</th><th class="n">Units</th><th class="n">Cases</th><th class="n">kg</th><th>Transport</th><th>Status</th></tr></thead>
    <tbody>${d.orders.map(orderRows).join('')}<tr class="total"><td colspan="3">Day total</td><td class="n">${num(d.units, 1)}</td><td class="n">${num(d.cases, 1)}</td><td class="n">${num(d.weight, 1)}</td><td colspan="2"></td></tr></tbody></table>
  </div>`).join('')}`;
};

const weekPage = (w, prefs, basis) => `<section class="week">
  <h1>Cashel Blue — Weekly Plan · ${esc(weekTitle(w))}</h1>
  <div class="sub">${esc(weekRange(w))} · by ${esc(basis)} · ${esc(optionLabel(INCLUDES, prefs.include).toLowerCase())}</div>
  <div class="stats">
    <div class="stat"><b>${w.order_count}</b><span>Orders</span></div>
    <div class="stat"><b>${num(w.units, 1)}</b><span>Units</span></div>
    <div class="stat"><b>${num(w.cases, 1)}</b><span>Cases</span></div>
    <div class="stat"><b>${num(w.weight, 0)} kg</b><span>Weight</span></div>
  </div>
  ${prefs.layout !== 'orders' ? gridSection(w) : ''}
  ${prefs.layout !== 'grid' ? ordersSection(w) : ''}
  <div class="foot">Generated ${format(new Date(), 'dd/MM/yyyy HH:mm')}</div>
</section>`;

export const printWeeklyPlan = (plan, prefs) => {
  const basis = optionLabel(DATE_FIELDS, plan.date_field).toLowerCase();
  const title = plan.weeks.length === 1 ? weekTitle(plan.weeks[0]) : `${weekTitle(plan.weeks[0])} + ${plan.weeks.length - 1} more`;
  const html = `<html><head><title>Weekly Plan - ${esc(title)}</title><style>${STYLE}</style></head>
    <body>${plan.weeks.map((w) => weekPage(w, prefs, basis)).join('')}</body></html>`;
  return openPrintWindow(html);
};
